import { TableBody } from '@material-ui/core';
import {
  AgentInLenderContext,
  AgentInSinglePaymentProviderContext,
  getAgentById,
  getPricingsByAgentId,
  Lender,
  MerchantStructure,
} from '@nemuru-eng/js-api-client';
import { ComponentType, useState } from 'react';

import { CredentialsType } from '@/components/modules/AgentDetail/ProvidersDetails/components/Credentials/types/CredentialsType';
import { AddCredentialsDialogFactory } from '@/components/modules/AgentDetail/ProvidersDetails/shared/AddCredentialsDialogFactory';
import { DeleteCredentialsDialog } from '@/components/modules/AgentDetail/ProvidersDetails/shared/DeleteCredentialsDialog';
import { EditCredentialsDialogFactory } from '@/components/modules/AgentDetail/ProvidersDetails/shared/EditCredentialsDialogFactory';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useCache } from '@/hooks/requests/useCache';
import { DefaultTableProps } from '@/types/defaultTableProps';
export enum CredentialsTableMenuActions {
  EDIT = 'edit',
  DELETE = 'delete',
}
interface TableBodyWrapperProps {
  agentId: string;
  channelId: string;
  agentInContextArray: AgentInLenderContext[] | AgentInSinglePaymentProviderContext[];
  merchantStructure: MerchantStructure;
  tableProps: Omit<DefaultTableProps, 'isScrollable'>;
  lenders: Lender[];
  credentialsType: CredentialsType;
  hideColumnsForDeleteMode?: boolean;
}
export default function withTableBodyWrapper<T>(TableBodyContent: ComponentType<T>) {
  return ({
    agentId,
    channelId,
    agentInContextArray,
    lenders,
    merchantStructure,
    tableProps,
    hideColumnsForDeleteMode,
    credentialsType,
    ...moduleProps
  }: TableBodyWrapperProps) => {
    const { data: agent } = useCache(getAgentById, { agentId });
    const { data: agentPricings } = useCache(getPricingsByAgentId, { agentId });
    const { opened, toggleDialog, setOpened } = useDialog();

    const [menuAction, setMenuAction] = useState<CredentialsTableMenuActions | undefined>();
    const [selectedAgentCredentials, setSelectedAgentCredentials] = useState<
      AgentInLenderContext | AgentInSinglePaymentProviderContext | undefined
    >();

    if (!agent) {
      return null;
    }

    const handleDeleteCredentials = ({
      agentCredentials,
    }: {
      agentCredentials: AgentInLenderContext | AgentInSinglePaymentProviderContext;
    }) => {
      setSelectedAgentCredentials(agentCredentials);
      setMenuAction(CredentialsTableMenuActions.DELETE);
      toggleDialog();
    };

    const handleEditCredentials = ({
      agentCredentials,
    }: {
      agentCredentials: AgentInLenderContext | AgentInSinglePaymentProviderContext;
    }) => {
      setSelectedAgentCredentials(agentCredentials);
      setMenuAction(CredentialsTableMenuActions.EDIT);
      toggleDialog();
    };

    const injectedProps = {
      agentInContextArray,
      handleEditCredentials,
      handleDeleteCredentials,
      agentPricings,
      lenders,
      tableProps,
      hideColumnsForDeleteMode,
    };

    return (
      <>
        <TableBody>
          <TableBodyContent {...(moduleProps as T)} {...injectedProps} />
        </TableBody>
        {menuAction === CredentialsTableMenuActions.EDIT && selectedAgentCredentials && opened && (
          <EditCredentialsDialogFactory
            opened={opened}
            setOpened={setOpened}
            toggleDialog={toggleDialog}
            agent={agent}
            channelId={channelId}
            lenders={lenders}
            merchantStructure={merchantStructure}
            selectedCredentials={selectedAgentCredentials}
            credentialsType={credentialsType}
          />
        )}
        {menuAction === CredentialsTableMenuActions.EDIT && !selectedAgentCredentials && opened && (
          <AddCredentialsDialogFactory
            opened={opened}
            setOpened={setOpened}
            toggleDialog={toggleDialog}
            agentId={agent.id}
            credentialsType={credentialsType}
            channelId={channelId}
            lenders={lenders}
            merchantStructure={merchantStructure}
          />
        )}
        {menuAction === CredentialsTableMenuActions.DELETE && selectedAgentCredentials && (
          <DeleteCredentialsDialog
            opened={opened}
            setOpened={setOpened}
            toggleDialog={toggleDialog}
            agentId={agent.id}
            channelId={channelId}
            merchantId={merchantStructure.merchant.id}
            lenders={lenders}
            credentialsType={credentialsType}
            selectedAgentCredentials={[selectedAgentCredentials]}
          />
        )}
      </>
    );
  };
}
